import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { templateApi } from './api';
import { templateQueryKeys } from './query-keys';
import { resolveTemplate } from './resolver';
import type { ResolvedTemplate, UserTemplate, SystemTemplate } from './types';

/**
 * useTemplateCache — Preloads all user + system templates at app init.
 *
 * Mount this hook once at root level (e.g., in App.tsx or after auth).
 * Data is cached by TanStack Query; subsequent calls use the cache.
 *
 * Stale time: 5 minutes — templates are user-authored content, not real-time.
 * Refetch: on window focus (to catch edits in another tab).
 */
export function useTemplateCache() {
  const userQuery = useQuery({
    queryKey:  templateQueryKeys.userTemplateList({ active_only: true }),
    queryFn:   () => templateApi.listUserTemplates({ active_only: true, limit: 200 }),
    staleTime: 5 * 60 * 1000,   // 5 minutes
    gcTime:    30 * 60 * 1000,  // 30 minutes
    refetchOnWindowFocus: true,
  });

  const systemQuery = useQuery({
    queryKey:  templateQueryKeys.systemTemplateList(),
    queryFn:   templateApi.listSystemTemplates,
    staleTime: 60 * 60 * 1000,  // 1 hour — system templates rarely change
    gcTime:    24 * 60 * 60 * 1000,
  });

  return {
    userTemplates:   userQuery.data?.templates ?? [] as UserTemplate[],
    systemTemplates: systemQuery.data?.templates ?? [] as SystemTemplate[],
    isLoading:       userQuery.isLoading || systemQuery.isLoading,
    isError:         userQuery.isError || systemQuery.isError,
  };
}

/**
 * useResolveTemplate — Resolves best template for a given tool name.
 *
 * Uses the cached user + system templates (no extra network call).
 * Falls back to system universal if no match found.
 *
 * @param toolName - The tool name (e.g., "internet_search")
 */
export function useResolveTemplate(toolName: string): ResolvedTemplate | null {
  const { userTemplates, systemTemplates, isLoading } = useTemplateCache();

  if (isLoading) return null;
  return resolveTemplate(toolName, userTemplates, systemTemplates);
}

/**
 * useInvalidateTemplates — Returns a function to invalidate template cache.
 *
 * Call after create/update/delete operations so the cache refreshes.
 * This triggers a re-resolve on the next render.
 */
export function useInvalidateTemplates() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: templateQueryKeys.userTemplates(),
    });
    // Also invalidate any per-tool resolved queries that may be cached
    queryClient.invalidateQueries({
      queryKey: templateQueryKeys.all,
    });
  }, [queryClient]);
}
